import { useCallback, useEffect, useMemo, useState } from 'react';
import { riyadhBusinessDate } from '@room-verify/shared';
import {
  API_URL,
  api,
  loadToken,
  saveToken,
  type InspectionListItem,
  type ListFilters,
  type Report,
  type SessionUser,
} from './api';
import { ar } from './ar';
import { IconShield } from './components/icons';
import { ToastRegion } from './components/primitives';
import { AppShell, type RouteId } from './components/shell';
import { useToasts } from './hooks';
import { EditDialog, type EditTarget } from './screens/EditDialog';
import {
  AuditScreen,
  InspectionsScreen,
  ReportsScreen,
  RoomsScreen,
  SettingsScreen,
  SupervisorsScreen,
} from './screens/Lists';
import { LoginScreen } from './screens/Login';
import { OverviewScreen } from './screens/Overview';
import { ReportScreen } from './screens/Report';

/**
 * Hash routing, no router dependency.
 *
 * Seven destinations, each backed by an endpoint that already exists. The hash keeps
 * deep links working on Pages without any server-side rewrite rules.
 */
type Route =
  | { name: RouteId; query?: URLSearchParams }
  | { name: 'report'; id: string };

const parseRoute = (): Route => {
  const raw = window.location.hash.replace(/^#/, '') || '/';
  const [path, search] = raw.split('?');
  const query = new URLSearchParams(search ?? '');

  const report = /^\/report\/([A-Za-z0-9-]{1,64})$/.exec(path ?? '');
  if (report?.[1]) return { name: 'report', id: report[1] };

  switch (path) {
    case '/inspections':
      return { name: 'inspections', query };
    case '/reports':
      return { name: 'reports', query };
    case '/rooms':
      return { name: 'rooms', query };
    case '/supervisors':
      return { name: 'supervisors', query };
    case '/audit':
      return { name: 'audit', query };
    case '/settings':
      return { name: 'settings', query };
    default:
      return { name: 'overview', query };
  }
};

/** Which nav item is lit — a report belongs to the archive it came from. */
const activeNav = (route: Route): RouteId => (route.name === 'report' ? 'reports' : route.name);

export default function App() {
  const [token, setToken] = useState<string | null>(loadToken);
  const [user, setUser] = useState<SessionUser | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [booting, setBooting] = useState(true);
  const [route, setRoute] = useState<Route>(parseRoute);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const [date, setDate] = useState(() => riyadhBusinessDate());
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const { toasts, push } = useToasts();

  useEffect(() => {
    const onHashChange = () => setRoute(parseRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  /* --------------------------------------------------------- session boot */

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const stored = loadToken();
      if (!stored) {
        setBooting(false);
        return;
      }
      try {
        const { user: me } = await api.me(stored);
        if (cancelled) return;
        if (me.role !== 'MANAGEMENT_ADMIN') {
          saveToken(null);
          setToken(null);
        } else {
          setUser(me);
          setToken(stored);
        }
      } catch {
        if (!cancelled) {
          saveToken(null);
          setToken(null);
        }
      } finally {
        if (!cancelled) setBooting(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleSignedIn = useCallback(
    (newToken: string, newUser: SessionUser, newExpiry: string) => {
      saveToken(newToken);
      setToken(newToken);
      setUser(newUser);
      setExpiresAt(newExpiry);
    },
    [],
  );

  const signOut = useCallback(async () => {
    const current = token;
    saveToken(null);
    setToken(null);
    setUser(null);
    window.location.hash = '';
    if (current) {
      try {
        await api.logout(current);
      } catch {
        /* the local session is already cleared */
      }
    }
  }, [token]);

  const handleAuthExpired = useCallback(() => {
    saveToken(null);
    setToken(null);
    setUser(null);
    push(ar.errSessionExpired, 'critical');
  }, [push]);

  /* -------------------------------------------------------------- actions */

  const navigate = useCallback((hash: string) => {
    window.location.hash = hash;
  }, []);

  const openReport = useCallback((id: string) => navigate(`#/report/${id}`), [navigate]);
  const back = useCallback(() => {
    if (window.history.length > 1) window.history.back();
    else navigate('#/reports');
  }, [navigate]);

  const startEdit = useCallback((item: InspectionListItem) => {
    setEditTarget({
      id: item.id,
      roomNumber: item.roomNumber,
      inspectionType: item.inspectionType,
      itemsComplete: item.itemsComplete,
      missingItems: item.missingItems,
      otherMissingItem: item.otherMissingItem,
    });
    setEditOpen(true);
  }, []);

  const startEditFromReport = useCallback((report: Report) => {
    setEditTarget({
      id: report.inspection.id,
      roomNumber: report.inspection.roomNumber,
      inspectionType: report.inspection.inspectionType,
      itemsComplete: report.inspection.itemsComplete,
      missingItems: report.inspection.missingItems,
      otherMissingItem: report.inspection.otherMissingItem,
    });
    setEditOpen(true);
  }, []);

  const onSaved = useCallback(
    (message: string) => {
      setEditOpen(false);
      setEditTarget(null);
      // Force every open view to refetch so the correction and its audit entry are
      // visible immediately, not after a manual refresh.
      setReloadKey((key) => key + 1);
      push(message, 'success');
    },
    [push],
  );

  const reportFilters = useMemo<Partial<ListFilters>>(() => {
    if (route.name !== 'reports' || !route.query) return {};
    const missingItems = route.query.get('missingItems');
    const edited = route.query.get('edited');
    return {
      ...(missingItems === 'yes' || missingItems === 'no' ? { missingItems } : {}),
      ...(edited === 'yes' || edited === 'no' ? { edited } : {}),
    };
  }, [route]);

  /* ------------------------------------------------------------ rendering */

  if (booting) {
    return (
      <div className="boot">
        <span className="boot-mark" aria-hidden="true">
          <IconShield size={22} />
        </span>
        <span className="sr-only">{ar.appName}</span>
      </div>
    );
  }

  if (!token || !user) return <LoginScreen onSignedIn={handleSignedIn} />;

  const openDrawer = () => setDrawerOpen(true);
  const common = {
    token,
    onOpenDrawer: openDrawer,
    onAuthExpired: handleAuthExpired,
    onOpenReport: openReport,
    onEdit: startEdit,
    reloadKey,
  };

  return (
    <AppShell
      active={activeNav(route)}
      userName={user.fullName}
      drawerOpen={drawerOpen}
      onDrawerChange={setDrawerOpen}
      onNavigate={navigate}
      onSignOut={signOut}
    >
      {route.name === 'overview' && (
        <OverviewScreen
          token={token}
          date={date}
          onDateChange={setDate}
          onOpenReport={openReport}
          onEdit={startEdit}
          onNavigate={navigate}
          onOpenDrawer={openDrawer}
          onAuthExpired={handleAuthExpired}
          reloadKey={reloadKey}
        />
      )}

      {route.name === 'inspections' && <InspectionsScreen {...common} />}

      {route.name === 'reports' && (
        <ReportsScreen
          key={`reports-${JSON.stringify(reportFilters)}`}
          {...common}
          initialFilters={reportFilters}
        />
      )}

      {route.name === 'rooms' && (
        <RoomsScreen
          token={token}
          onOpenDrawer={openDrawer}
          onAuthExpired={handleAuthExpired}
          onOpenReport={openReport}
          reloadKey={reloadKey}
        />
      )}

      {route.name === 'supervisors' && (
        <SupervisorsScreen
          token={token}
          onOpenDrawer={openDrawer}
          onAuthExpired={handleAuthExpired}
          reloadKey={reloadKey}
        />
      )}

      {route.name === 'audit' && (
        <AuditScreen
          token={token}
          onOpenDrawer={openDrawer}
          onAuthExpired={handleAuthExpired}
          onOpenReport={openReport}
          reloadKey={reloadKey}
        />
      )}

      {route.name === 'settings' && (
        <SettingsScreen
          user={user}
          expiresAt={expiresAt}
          apiUrl={API_URL}
          onOpenDrawer={openDrawer}
          onSignOut={signOut}
        />
      )}

      {route.name === 'report' && (
        <ReportScreen
          token={token}
          inspectionId={route.id}
          onBack={back}
          onEdit={startEditFromReport}
          onAuthExpired={handleAuthExpired}
          reloadKey={reloadKey}
        />
      )}

      <EditDialog
        open={editOpen}
        token={token}
        target={editTarget}
        onClose={() => setEditOpen(false)}
        onSaved={onSaved}
        onAuthExpired={handleAuthExpired}
      />

      <ToastRegion toasts={toasts} />
    </AppShell>
  );
}
